import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import SqlSourceView from './SqlSourceView';
import { QueryViewButton, QueryViewDialog } from './QueryView';
import { FiRefreshCw, FiHardDrive, FiClock, FiInfo, FiX, FiHelpCircle } from 'react-icons/fi';
import PanelChart from './PanelChart';
import './ResourceAnalysisModal.css';
import ConnTabsWindow, { ConnTabPane } from './ConnTabsWindow';
import memoWindow from '../common/memoWindow';
import OwnConnTarget from './OwnConnTarget';
import useOwnConnSession, { ORACLE_CONN } from '../../hooks/useOwnConnSession';
import { formatApiError } from '../../utils/formatters';
import { groupByInst, NodeHead, UsageBarGrid, SgaTreemap, UndoBlocksBar, MemoryModeNote } from './ResourceViews';
import { groupParamsByInst } from '../../utils/memoryMode';

const API_ROOT = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/sql-developer\/?$/, '');
const RES_API = `${API_ROOT}/sql-developer/monitoring/resource-analysis`;

const getDaysOptions = (t) => [
    { value: 1, label: t('monitoring:res.range.day1') },
    { value: 3, label: t('monitoring:res.range.day3') },
    { value: 7, label: t('monitoring:res.range.day7') },
    { value: 14, label: t('monitoring:res.range.day14') },
];

// 경과 시간 — 트랜잭션이 열린 지 얼마나 됐나(초 → 1h 02m / 5m 20s / 42s)
function fmtElapsed(sec) {
    if (sec == null) return '-';
    const n = Math.max(0, Math.round(Number(sec)));
    const h = Math.floor(n / 3600);
    const m = Math.floor((n % 3600) / 60);
    const s = n % 60;
    if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
    if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
    return `${s}s`;
}

function fmtMb(v) {
    if (v == null) return '-';
    const n = Number(v);
    if (n >= 1024) return `${(n / 1024).toFixed(2)}GB`;
    // 작은 값을 0 으로 뭉개지 않는다 — 0.4MB 가 「0MB」 로 보이면 「안 쓴다」 로 읽힌다
    return n > 0 && n < 10 ? `${n.toFixed(1)}MB` : `${n.toFixed(0)}MB`;
}
function fmtPct(v) {
    if (v == null) return '-';
    return `${Number(v).toFixed(1)}%`;
}

/** 연결 하나의 본문 — 창(아래 ResourceAnalysisModal)이 DB 연결마다 탭으로 하나씩 띄운다(ConnTabsWindow, 2026-10-14) */
const ResourceAnalysisModalBody = ({ isOpen: windowOpen = false, onClose, connections = [], darkMode = false, defaultConnectionId, active = true, excludeConnIds = [], onConnectionChange }) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState('realtime'); // 'realtime' | 'history'
    // 스토리지·메모리는 Oracle 전용(DBA_HIST_*, V$SGASTAT 등) — Oracle 연결만 노출.
    // **기본은 지금 쓰고 있는 연결이다.** 목록의 첫 번째를 고르던 탓에 교육DB 가 잡혀,
    // 메뉴를 열면 남의 DB 를 조회하고 있었다(사용자 신고).
    //
    // 이 창은 열 때 조회 5개를 **동시에** 쏜다. 한 세션은 한 번에 한 문장만 돌리므로
    // 그대로 보내면 서버가 409 로 거절한다 — `run()` 이 줄을 세우고, 카드마다
    // 이미 있는 로딩 표시 덕에 하나씩 차례로 채워진다.
    const mon = useOwnConnSession({
        isOpen: windowOpen, connections, defaultConnectionId, label: 'resource-analysis',
        filter: ORACLE_CONN,
    });
    // 연결이 정해지기 전에는 조회하지 않는다(2026-10-14) — 연결 없이 나간 첫 조회에 서버가 예시 데이터를 주어,
    // 창을 열면 예시가 먼저 그려지고 실제 값이 그 위를 덮었다. 고를 연결이 아예 없을 때만 그대로 연다(예시 켜기·오류 안내가 보이게)
    const isOpen = windowOpen && (!!mon.connId || mon.conns.length === 0);
    // 탭 이름이 이 연결을 따라가게 창에 알린다(탭 안에서 연결을 바꿀 수 있다)
    useEffect(() => { if (mon.connId && onConnectionChange) onConnectionChange(mon.connId); }, [mon.connId]); // eslint-disable-line react-hooks/exhaustive-deps
    // 예시 데이터는 기본 꺼짐 — 사용자가 켤 때만(2026-10-03, 모든 창 공통)
    const [useDemo, setUseDemo] = useState(false);
    // 「설명 보기」 — 익숙한 사람은 풀이 문장을 걷고 수치만 본다. 이 PC 에 기억한다(예시 데이터와 달리 화면 취향이다)
    const [showNotes, setShowNotes] = useState(() => { try { return localStorage.getItem('livesql_resana_notes') !== '0'; } catch (_e) { return true; } });
    const toggleNotes = (on) => {
        setShowNotes(on);
        try { localStorage.setItem('livesql_resana_notes', on ? '1' : '0'); } catch (_e) { /* 기억 못 해도 동작한다 */ }
    };
    // 창을 열 때마다 예시는 꺼진 상태로 — 앞에서 켠 것이 남아 재현되지 않게(2026-10-03). 저장하지도 않는다
    useEffect(() => { if (isOpen) setUseDemo(false); }, [isOpen]);

    // 데모일 때는 세션을 싣지 않는다 — 백엔드가 DB 에 가지 않는다.
    // `mon` 을 통째로 의존성에 넣지 말 것 — 폴링으로 session 이 갱신될 때마다
    // 모든 조회가 다시 돈다. 고정된 것(run)과 값(sessionId·creating)만 꺼낸다.
    const { run, creating: monCreating } = mon;

    const target = useCallback((extra) => (useDemo
        ? { demo: 1, ...extra }
        : { ...mon.params, ...extra }), [useDemo, mon.params]);

    // 「이 수치가 어디서 왔나」 — 섹션마다 실제로 도는 SQL 을 서버에서 받아 보여 준다.
    // 사본을 화면에 두지 않는 이유: 사본은 반드시 어긋나고, 투명성이 목적인 기능에서
    // 어긋난 사본은 없느니만 못하다(실행계획 분석에서 이미 겪었다).
    const [queryViewKind, setQueryViewKind] = useState(null);
    const [queryViewSql, setQueryViewSql] = useState(null);

    // 나.6) 세그먼트 통계 — 집계 단위와 정렬 기준을 화면이 고른다.
    // 기본이 서브오브젝트(파티션) 단위여서 목록 20칸이 파티션 하나짜리 표로 가득 찼다.
    const [segGranularity, setSegGranularity] = useState('object');   // 'object' | 'subobject'
    const [segOrderBy, setSegOrderBy] = useState('logical_reads');
    // 테이블스페이스를 고르면 그 아래에 그것을 차지하는 세그먼트(2026-10-03). 단위는 세그먼트 통계와 같은 두 갈래
    const [selectedTs, setSelectedTs] = useState(null);
    const [tsSegGran, setTsSegGran] = useState('object');   // 'object' | 'subobject'
    const [tsSeg, setTsSeg] = useState(null);                // { rows, total, maxRows }
    const [tsSegLoading, setTsSegLoading] = useState(false);
    const [tsSegError, setTsSegError] = useState(null);

    // 「쿼리」를 누르면 그때 받아 온다. 조건(집계 단위·정렬)이 바뀌면 SQL 도 달라지므로
    // 캐시하지 않는다 — 지금 이 목록이 무엇으로 나왔는지에 답해야 하는 기능이다.
    useEffect(() => {
        if (!queryViewKind) { setQueryViewSql(null); return; }
        let alive = true;
        setQueryViewSql(null);
        axios.get(`${RES_API}/source-sql`, {
            params: {
                kind: queryViewKind, granularity: queryViewKind === 'ts-segments' ? tsSegGran : segGranularity, orderBy: segOrderBy,
                // 테이블스페이스를 고르지 않았으면 전체를 보는 문장이다(2026-10-16)
                ...(queryViewKind === 'ts-segments' && !selectedTs ? { all: '1' } : {}),
            },
        })
            .then(r => { if (alive) setQueryViewSql(r.data?.sql || ''); })
            .catch(err => { if (alive) setQueryViewSql({ error: formatApiError(err, t) }); });
        return () => { alive = false; };
    }, [queryViewKind, segGranularity, segOrderBy, tsSegGran, selectedTs, t]);

    // 섹션 제목 옆의 「도움말」·「쿼리」 버튼 — 섹션마다 같은 마크업이라 함수로 뺀다. 도움말은 쿼리 오른편(2026-10-03 요청):
    // 이 칸이 무엇을 보이는지 · 어떻게 읽는지 · 주의할 점 · 어느 뷰를 읽는지. 문구는 monitoring:res.help.<섹션>
    const [helpKind, setHelpKind] = useState(null);
    const helpKey = (kind) => String(kind).replace(/-([a-z])/g, (_m, c) => c.toUpperCase());   // buffer-usage → bufferUsage
    const queryBtn = (kind) => (
        <>
            <QueryViewButton onClick={() => setQueryViewKind(kind)} title={t('monitoring:res.viewQueryNote')} />
            <button
                type="button"
                className="resana-btn resana-btn-icon"
                onClick={() => setHelpKind(kind)}
                title={t('monitoring:res.helpUi.button')}
                aria-label={t('monitoring:res.helpUi.button')}
            >
                <FiHelpCircle size={11} />
            </button>
        </>
    );

    // ===== 탭 A: 실시간 사용량 =====
    // 섹션별로 표시/숨김 토글 — 새로고침 버튼 오른편 아이콘 버튼, 이 행은 스크롤 영역 밖(sticky)에 둔다.
    // 리본은 **탭처럼 하나만** 보인다(2026-10-03 요청 — 예전에는 켜고 끄는 토글이라 여러 섹션이 한 화면에 쌓였다).
    // 모양(키별 참/거짓)은 그대로 두어 아래 `visibleSections.x &&` 들이 손대지 않고 동작한다.
    const [visibleSections, setVisibleSections] = useState({ storage: true });
    const toggleSection = (key) => setVisibleSections({ [key]: true });


    // 섹션별 실패는 그 섹션만 "로드 실패"로 남기고 재시도 버튼을 둔다 — 예전엔 하나라도 실패하면
    // 전역 useDemo 를 true 로 되돌려서, 이미 성공한 다른 섹션까지 데모 데이터로 덮어써버렸다.
    // (예: 세그먼트 조회 권한이 없는 연결에서 그 실패가 스토리지/메모리까지 데모로 되돌리던 원인.
    //  "예시 데이터 언첵이 안 된다"는 팀장 피드백이 이 연쇄 반전 때문이었다.)
    const [storageRows, setStorageRows] = useState([]);
    const [storageLoading, setStorageLoading] = useState(false);
    const [storageError, setStorageError] = useState(null);
    const [storageIsDemo, setStorageIsDemo] = useState(false);
    const [storageSortBy, setStorageSortBy] = useState('usedPct'); // 'usedPct' | 'usedMb' | 'freeMb'
    const [storageShowAll, setStorageShowAll] = useState(false);
    const [memorySga, setMemorySga] = useState([]);
    const [memoryPga, setMemoryPga] = useState([]);
    const [memoryLoading, setMemoryLoading] = useState(false);
    const [memoryError, setMemoryError] = useState(null);
    const [memoryIsDemo, setMemoryIsDemo] = useState(false);
    const [memoryBufferPools, setMemoryBufferPools] = useState([]);
    const [memoryParams, setMemoryParams] = useState([]);
    const [pgaConsumers, setPgaConsumers] = useState([]);   // PGA 를 많이 쥔 세션 상위 20
    const [pgaConsumersError, setPgaConsumersError] = useState(null);   // 관리 방식(자동/수동) 판정용 — GV$PARAMETER
    // 버퍼 캐시 실제 사용(gv$bh) — **누를 때만**(무겁다). 연결·예시가 바뀌면 지운다
    const [bufferUsage, setBufferUsage] = useState(null);
    const [bufferUsageLoading, setBufferUsageLoading] = useState(false);
    const [bufferUsageError, setBufferUsageError] = useState(null);

    const fetchStorage = useCallback(async () => {
        if (monCreating) return;
        setStorageLoading(true);
        setStorageError(null);
        try {
            const res = await run(() => axios.get(`${RES_API}/storage`, {
                params: target({}),
            }));
            setStorageRows(res.data?.data || []);
            setStorageIsDemo(res.data?.isDemo !== false);
        } catch (err) {
            setStorageError(err.message);
        } finally {
            setStorageLoading(false);
        }
    }, [run, target, monCreating]);

    const fetchMemory = useCallback(async () => {
        if (monCreating) return;
        setMemoryLoading(true);
        setMemoryError(null);
        try {
            const res = await run(() => axios.get(`${RES_API}/memory`, {
                params: target({}),
            }));
            setMemorySga(res.data?.sga || []);
            setMemoryPga(res.data?.pga || []);
            setMemoryBufferPools(res.data?.bufferPools || []);
            setMemoryParams(res.data?.memoryParams || []);
            setPgaConsumers(res.data?.pgaConsumers || []);
            setPgaConsumersError(res.data?.pgaConsumersError || null);
            setBufferUsage(null);
            setMemoryIsDemo(res.data?.isDemo !== false);
        } catch (err) {
            setMemoryError(err.message);
        } finally {
            setMemoryLoading(false);
        }
    }, [run, target, monCreating]);

    // UNDO 는 노드마다 자기 테이블스페이스를 쓴다 — [{ instId, latest, tablespace }]
    const [undoNodes, setUndoNodes] = useState([]);
    const [undoTx, setUndoTx] = useState([]);   // UNDO 를 많이 쓰는 진행 중 트랜잭션 상위 20
    const [undoTxError, setUndoTxError] = useState(null);
    const [undoLoading, setUndoLoading] = useState(false);
    const [undoError, setUndoError] = useState(null);
    const [undoIsDemo, setUndoIsDemo] = useState(false);
    const [tempTablespaces, setTempTablespaces] = useState([]);
    const [tempConsumers, setTempConsumers] = useState([]);
    const [tempLoading, setTempLoading] = useState(false);
    const [tempError, setTempError] = useState(null);
    const [tempIsDemo, setTempIsDemo] = useState(false);
    const [segmentRows, setSegmentRows] = useState([]);
    const [segmentLoading, setSegmentLoading] = useState(false);
    const [segmentError, setSegmentError] = useState(null);
    const [segmentIsDemo, setSegmentIsDemo] = useState(false);
    const [segmentShowAll, setSegmentShowAll] = useState(false);

    // 기본은 **모든 테이블스페이스**, 위 목록에서 고르면 그것으로 거른다(2026-10-16 사용자 요청 — 예전에는 고르기 전까지 비어 있었다)
    const fetchTsSegments = useCallback(async () => {
        if (monCreating) return;
        setTsSegLoading(true);
        setTsSegError(null);
        try {
            const res = await run(() => axios.get(`${RES_API}/tablespace-segments`, {
                params: target({ ...(selectedTs ? { tablespace: selectedTs } : {}), granularity: tsSegGran }),
            }));
            setTsSeg({ rows: res.data?.rows || [], total: res.data?.total || null, isDemo: res.data?.isDemo !== false });
        } catch (err) {
            setTsSeg(null);
            setTsSegError(formatApiError(err, t));
        } finally {
            setTsSegLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [run, target, monCreating, selectedTs, tsSegGran]);
    // 고르거나 단위·예시·연결을 바꾸면 다시 읽는다
    useEffect(() => { if (isOpen) fetchTsSegments(); }, [isOpen, selectedTs, fetchTsSegments]);
    // 연결이 바뀌면 고른 테이블스페이스는 그 DB 의 것이 아니다 — 전체로 돌아간다
    useEffect(() => { setSelectedTs(null); }, [mon.connId]);
    // 예시 ↔ 실제를 바꾸면 고른 테이블스페이스가 그쪽에 없을 수 있다 — 고름을 푼다
    useEffect(() => { setSelectedTs(null); setTsSeg(null); }, [useDemo]);

    // SQL_ID 를 누르면 그 표 아래에 원문(2026-10-03) — PGA·UNDO·TEMP 상위 목록 공통. 같은 것을 다시 누르면 닫는다.
    // { from: 'pga'|'undo'|'temp', sqlId, text, loading, error }
    const [sqlTextView, setSqlTextView] = useState(null);
    const openSqlText = useCallback(async (from, sqlId) => {
        if (!sqlId) return;
        if (sqlTextView && sqlTextView.from === from && sqlTextView.sqlId === sqlId) { setSqlTextView(null); return; }
        setSqlTextView({ from, sqlId, loading: true });
        try {
            const res = await run(() => axios.get(`${RES_API}/sql-text`, { params: target({ sqlId }) }));
            setSqlTextView(v => (v && v.sqlId === sqlId && v.from === from
                ? { from, sqlId, text: res.data?.sqlText ?? null, isDemo: res.data?.isDemo === true } : v));
        } catch (err) {
            setSqlTextView(v => (v && v.sqlId === sqlId && v.from === from ? { from, sqlId, error: formatApiError(err, t) } : v));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [run, target, sqlTextView]);
    // 예시 ↔ 실제를 바꾸면 보던 원문은 다른 DB 의 것이 된다 — 닫는다
    useEffect(() => { setSqlTextView(null); }, [useDemo]);

    const fetchUndo = useCallback(async () => {
        if (monCreating) return;
        setUndoLoading(true);
        setUndoError(null);
        try {
            const res = await run(() => axios.get(`${RES_API}/undo`, {
                params: target({}),
            }));
            const nodes = res.data?.nodes
                || (res.data?.latest ? [{ instId: 1, latest: res.data.latest, tablespace: res.data.tablespace || null }] : []);
            setUndoNodes(nodes);
            setUndoTx(res.data?.topTransactions || []);
            setUndoTxError(res.data?.topTransactionsError || null);
            setUndoIsDemo(res.data?.isDemo !== false);
        } catch (err) {
            setUndoError(err.message);
        } finally {
            setUndoLoading(false);
        }
    }, [run, target, monCreating]);

    const fetchTemp = useCallback(async () => {
        if (monCreating) return;
        setTempLoading(true);
        setTempError(null);
        try {
            const res = await run(() => axios.get(`${RES_API}/temp`, {
                params: target({}),
            }));
            setTempTablespaces(res.data?.tablespaces || []);
            setTempConsumers(res.data?.topConsumers || []);
            setTempIsDemo(res.data?.isDemo !== false);
        } catch (err) {
            setTempError(err.message);
        } finally {
            setTempLoading(false);
        }
    }, [run, target, monCreating]);

    const fetchSegments = useCallback(async () => {
        if (monCreating) return;
        setSegmentLoading(true);
        setSegmentError(null);
        try {
            const res = await run(() => axios.get(`${RES_API}/segments`, {
                params: target({ granularity: segGranularity, orderBy: segOrderBy }),
            }));
            setSegmentRows(res.data?.data || []);
            setSegmentIsDemo(res.data?.isDemo !== false);
        } catch (err) {
            setSegmentError(err.message);
        } finally {
            setSegmentLoading(false);
        }
    }, [run, target, monCreating, segGranularity, segOrderBy]);

    // 세그먼트는 따로 — 정렬·단위를 바꿀 때 다른 네 섹션까지 다시 읽던 것(fetchSegments 가 의존성에 있었다)
    useEffect(() => {
        if (!isOpen || activeTab !== 'realtime') return;
        fetchStorage();
        fetchMemory();
        fetchUndo();
        fetchTemp();
    }, [isOpen, activeTab, fetchStorage, fetchMemory, fetchUndo, fetchTemp]);
    useEffect(() => {
        if (!isOpen || activeTab !== 'realtime') return;
        fetchSegments();
    }, [isOpen, activeTab, fetchSegments]);

    const fetchBufferUsage = useCallback(async () => {
        setBufferUsageLoading(true);
        setBufferUsageError(null);
        try {
            const res = await run(() => axios.get(`${RES_API}/buffer-usage`, { params: target({}) }));
            setBufferUsage(res.data?.data || []);
        } catch (err) {
            setBufferUsageError(formatApiError(err, t));
        } finally {
            setBufferUsageLoading(false);
        }
    }, [run, target, t]);

    // ===== 탭 B: 이력 추이 (DBA_HIST_*, UNDO 는 V$UNDOSTAT) =====
    const [historyDays, setHistoryDays] = useState(7);
    // 그래프별 표시/숨김 — 새로고침 버튼 오른편, 이 행도 sticky.
    const [visibleTrends, setVisibleTrends] = useState({ storage: true });   // 이력 탭도 하나만
    const toggleTrend = (key) => setVisibleTrends({ [key]: true });

    const [storageTrend, setStorageTrend] = useState([]);
    const [storageTrendSeries, setStorageTrendSeries] = useState([]);
    const [storageTrendLoading, setStorageTrendLoading] = useState(false);
    const [storageTrendError, setStorageTrendError] = useState(null);
    const [memoryTrend, setMemoryTrend] = useState([]);
    const [memoryTrendSeries, setMemoryTrendSeries] = useState([]);
    const [memoryTrendLoading, setMemoryTrendLoading] = useState(false);
    const [memoryTrendError, setMemoryTrendError] = useState(null);
    const [undoTrend, setUndoTrend] = useState([]);
    const [undoTrendSeries, setUndoTrendSeries] = useState([]);
    const [undoTrendLoading, setUndoTrendLoading] = useState(false);
    const [undoTrendError, setUndoTrendError] = useState(null);
    const [segmentTrend, setSegmentTrend] = useState([]);
    const [segmentTrendSeries, setSegmentTrendSeries] = useState([]);
    const [segmentTrendLoading, setSegmentTrendLoading] = useState(false);
    const [segmentTrendError, setSegmentTrendError] = useState(null);

    const fetchStorageTrend = useCallback(async () => {
        if (monCreating) return;
        setStorageTrendLoading(true);
        setStorageTrendError(null);
        try {
            const res = await run(() => axios.get(`${RES_API}/storage-trend`, {
                params: target({ days: historyDays }),
            }));
            setStorageTrend(res.data?.data || []);
            setStorageTrendSeries(res.data?.seriesKeys || []);
        } catch (err) {
            setStorageTrendError(err.message);
        } finally {
            setStorageTrendLoading(false);
        }
    }, [run, target, monCreating, historyDays]);

    const fetchMemoryTrend = useCallback(async () => {
        if (monCreating) return;
        setMemoryTrendLoading(true);
        setMemoryTrendError(null);
        try {
            const res = await run(() => axios.get(`${RES_API}/memory-trend`, {
                params: target({ days: historyDays }),
            }));
            setMemoryTrend(res.data?.data || []);
            setMemoryTrendSeries(res.data?.seriesKeys || []);
        } catch (err) {
            setMemoryTrendError(err.message);
        } finally {
            setMemoryTrendLoading(false);
        }
    }, [run, target, monCreating, historyDays]);

    // TEMP 는 실시간만 있었는데, 정작 알고 싶은 것은 "언제 터졌나" 다(다.1).
    const [tempTrend, setTempTrend] = useState([]);
    const [tempTrendSeries, setTempTrendSeries] = useState([]);
    const [tempTrendLoading, setTempTrendLoading] = useState(false);
    const [tempTrendError, setTempTrendError] = useState(null);
    const fetchTempTrend = useCallback(async () => {
        if (monCreating) return;
        setTempTrendLoading(true);
        setTempTrendError(null);
        try {
            const res = await run(() => axios.get(`${RES_API}/temp-trend`, {
                params: target({ days: historyDays }),
            }));
            setTempTrend(res.data?.data || []);
            setTempTrendSeries(res.data?.seriesKeys || []);
        } catch (err) {
            setTempTrendError(err.message);
        } finally {
            setTempTrendLoading(false);
        }
    }, [run, target, monCreating, historyDays]);

    const fetchUndoTrend = useCallback(async () => {
        if (monCreating) return;
        setUndoTrendLoading(true);
        setUndoTrendError(null);
        try {
            const res = await run(() => axios.get(`${RES_API}/undo-trend`, {
                params: target({ days: historyDays }),
            }));
            setUndoTrend(res.data?.data || []);
            setUndoTrendSeries(res.data?.seriesKeys || []);
        } catch (err) {
            setUndoTrendError(err.message);
        } finally {
            setUndoTrendLoading(false);
        }
    }, [run, target, monCreating, historyDays]);

    const fetchSegmentTrend = useCallback(async () => {
        if (monCreating) return;
        setSegmentTrendLoading(true);
        setSegmentTrendError(null);
        try {
            const res = await run(() => axios.get(`${RES_API}/segments-trend`, {
                params: target({ days: historyDays }),
            }));
            setSegmentTrend(res.data?.data || []);
            setSegmentTrendSeries(res.data?.seriesKeys || []);
        } catch (err) {
            setSegmentTrendError(err.message);
        } finally {
            setSegmentTrendLoading(false);
        }
    }, [run, target, monCreating, historyDays]);

    useEffect(() => {
        if (!isOpen || activeTab !== 'history') return;
        fetchStorageTrend();
        fetchMemoryTrend();
        fetchUndoTrend();
        fetchTempTrend();
        fetchSegmentTrend();
    }, [isOpen, activeTab, fetchStorageTrend, fetchMemoryTrend, fetchUndoTrend, fetchTempTrend, fetchSegmentTrend]);

    // SQL_ID 칸 — 누르면 아래에 원문. 지금 펼친 것은 표시한다
    const sqlIdCell = (from, sqlId) => (sqlId ? (
        <button
            type="button"
            className={`resana-sqlid-link ${sqlTextView && sqlTextView.from === from && sqlTextView.sqlId === sqlId ? 'on' : ''}`}
            onClick={() => openSqlText(from, sqlId)}
            title={t('monitoring:res.sqlText.clickHint')}
        >
            {sqlId}
        </button>
    ) : '-');
    const sqlTextPanel = (from) => (sqlTextView && sqlTextView.from === from ? (
        <div className="resana-sqltext">
            <div className="resana-sqltext-head">
                <span>{t('monitoring:res.sqlText.title', { sqlId: sqlTextView.sqlId })}</span>
                {sqlTextView.isDemo && <span className="resana-demo-badge">{t('monitoring:res.demoData')}</span>}
                <button type="button" className="resana-btn resana-btn-icon" onClick={() => setSqlTextView(null)} title={t('monitoring:res.sqlText.close')}>
                    <FiX size={11} />
                </button>
            </div>
            {sqlTextView.loading ? (
                <div className="resana-empty-note">{t('monitoring:res.loading')}</div>
            ) : sqlTextView.error ? (
                <div className="resana-section-error"><FiInfo size={13} /> {sqlTextView.error}</div>
            ) : sqlTextView.text == null ? (
                <div className="resana-empty-note">{t('monitoring:res.sqlText.gone')}</div>
            ) : (
                <SqlSourceView className="resana-sqltext-body" sql={sqlTextView.text} loadingText="" />
            )}
        </div>
    ) : null);

    // 「쿼리」 창 — 이 섹션이 실제로 돌리는 SQL. 조건이 바뀌면 내용도 따라 바뀐다.
    const queryViewer = queryViewKind && (
        <QueryViewDialog
            title={t('monitoring:res.viewQueryTitle')}
            sql={typeof queryViewSql === 'string' ? queryViewSql : null}
            error={queryViewSql && queryViewSql.error}
            loadingText={t('monitoring:res.viewQueryLoading')}
            note={t('monitoring:res.viewQueryNote')}
            onClose={() => setQueryViewKind(null)}
        />
    );

    // 「도움말」 창 — 쿼리 창과 같은 겉모양. 줄바꿈으로 나눈 문구를 항목으로 보인다
    const helpLines = (key) => String(t(key)).split('\n').filter(Boolean);
    const helpViewer = helpKind && (() => {
        const base = `monitoring:res.help.${helpKey(helpKind)}`;
        return (
            <div className="resana-queryview-overlay" onClick={() => setHelpKind(null)}>
                <div className="app-modal resana-queryview-panel resana-help-panel" data-app-modal onClick={(e) => e.stopPropagation()}>
                    <div className="app-modal-header resana-queryview-header">
                        <span className="app-modal-title"><FiHelpCircle size={14} /> {t(`${base}.title`)}</span>
                        <button type="button" className="app-modal-close" onClick={() => setHelpKind(null)}>
                            <FiX size={16} />
                        </button>
                    </div>
                    <div className="resana-help-body">
                        <h4>{t('monitoring:res.helpUi.what')}</h4>
                        <p>{t(`${base}.what`)}</p>
                        <h4>{t('monitoring:res.helpUi.how')}</h4>
                        <ul>{helpLines(`${base}.how`).map((line, i) => <li key={i}>{line}</li>)}</ul>
                        <h4>{t('monitoring:res.helpUi.caution')}</h4>
                        <ul>{helpLines(`${base}.caution`).map((line, i) => <li key={i}>{line}</li>)}</ul>
                    </div>
                    <div className="resana-queryview-note">
                        <FiInfo size={12} /> {t('monitoring:res.helpUi.source')}: <code>{t(`${base}.source`)}</code>
                    </div>
                </div>
            </div>
        );
    })();

    if (!isOpen) return null;

    // 그림+표 한 격자(UsageBarGrid)의 머리글
    const gridLabels = {
        name: t('monitoring:res.tablespace'), barHint: t('monitoring:res.barColHint'),
        total: t('monitoring:res.total'), used: t('monitoring:res.used'), free: t('monitoring:res.free'), pct: t('monitoring:res.usagePct'),
    };

    // 테이블스페이스 — 정렬 기준(할당 · 사용 · 여유 · 사용률 — 표의 열 순서와 같게, 2026-10-03 요청) + 최대 5개만 표시,
    // "전체보기"로 확장. 「할당」 = 데이터파일에 지금 잡힌 크기 합(dba_data_files.bytes — 자동 확장 최대치가 아니다).
    // 막대도 고른 기준의 값을 그린다(요청: 률 또는 양) — 순서만 바뀌고 막대가 사용률 그대로면 무엇을 보는지 모른다.
    const STORAGE_SORT_OPTIONS = [
        { value: 'totalMb', label: t('monitoring:res.total') },
        { value: 'usedMb', label: t('monitoring:res.used') },
        { value: 'freeMb', label: t('monitoring:res.free') },
        { value: 'usedPct', label: t('monitoring:res.usagePct') },
    ];
    const sortedStorageRows = [...storageRows].sort((a, b) => (b[storageSortBy] || 0) - (a[storageSortBy] || 0));
    const displayedStorageRows = storageShowAll ? sortedStorageRows : sortedStorageRows.slice(0, 5);
    const displayedSegmentRows = segmentShowAll ? segmentRows : segmentRows.slice(0, 5);

    // 나.6) 지표별로 정렬한 결과를 **그림으로도** 본다. 막대는 지금 정렬 기준인 그 지표다 —
    // 정렬만 바꾸고 그림이 그대로면 무엇을 보고 있는지 알 수 없다.
    const SEGMENT_SORT_OPTIONS = (tr) => [
        { value: 'logical_reads', label: tr('monitoring:res.seg.logicalReads') },
        { value: 'physical_reads', label: tr('monitoring:res.seg.physicalReads') },
        { value: 'buffer_busy_waits', label: tr('monitoring:res.seg.bufferBusy') },
        { value: 'row_lock_waits', label: tr('monitoring:res.seg.rowLock') },
    ];
    const SEGMENT_FIELD = {
        logical_reads: 'logicalReads',
        physical_reads: 'physicalReads',
        buffer_busy_waits: 'bufferBusyWaits',
        row_lock_waits: 'rowLockWaits',
    };
    const segMetricField = SEGMENT_FIELD[segOrderBy] || 'logicalReads';
    // 표 머리는 예전 그대로(오라클 통계 이름) — 정렬 기준 열은 강조한다
    const SEG_COL_HEAD = { logical_reads: 'Logical reads', physical_reads: 'Physical reads', buffer_busy_waits: 'Buffer busy', row_lock_waits: 'Row lock' };
    const fmtCount = (v) => (v == null ? '-' : (Number(v).toLocaleString?.() ?? v));
    const segMetricLabel = (SEGMENT_SORT_OPTIONS(t).find(o => o.value === segOrderBy) || {}).label || '';
    // RAC — 같은 객체가 노드마다 한 줄씩 온다. 라벨에 노드가 없으면 그림에서 두 줄이 같은 이름이 되어
    // 「정렬을 바꿔도 그림의 객체가 그대로」로 보였다(신고). 노드가 둘 이상이면 [n] 을 붙인다.
    const segMulti = new Set(segmentRows.map(r => r.instId ?? 1)).size > 1;
    // 막대 길이 = 지금 보이는 줄 중 가장 큰 값 대비(정렬 기준 지표)
    const segBarMax = Math.max(1, ...displayedSegmentRows.map(r => Number(r[segMetricField]) || 0));
    const sgaNodes = groupByInst(memorySga);
    const pgaNodes = groupByInst(memoryPga);
    const memParamsByInst = groupParamsByInst(memoryParams);
    const tempNodes = groupByInst(tempTablespaces);
    const showAllBtn = (total, on, toggle) => (total > 5 ? (
        <button type="button" className={`resana-sort-btn resana-showall-top ${on ? 'on' : ''}`} onClick={toggle}>
            {on ? t('monitoring:res.collapseTo5') : t('monitoring:res.showAll', { n: total })}
        </button>
    ) : null);
    // 테이블스페이스가 수십 개인 DB 에서는 범례가 그래프를 통째로 덮어 선이 안 보였다.
    // **최고 사용률 상위 5개**만 그린다 — 나머지는 어차피 겹쳐서 읽을 수 없다.
    const TREND_TOP_N = 5;
    const topStorageTrendSeries = (() => {
        if (storageTrendSeries.length <= TREND_TOP_N) return storageTrendSeries;
        const peak = new Map();
        (storageTrend || []).forEach(row => {
            storageTrendSeries.forEach(k => {
                const v = Number(row[k]);
                if (Number.isFinite(v) && v > (peak.get(k) ?? -Infinity)) peak.set(k, v);
            });
        });
        return [...storageTrendSeries]
            .sort((a, b) => (peak.get(b) ?? 0) - (peak.get(a) ?? 0))
            .slice(0, TREND_TOP_N);
    })();
    const storageTrendPanel = { chartType: 'line', xField: 't', yFields: topStorageTrendSeries, yAxis: { title: t('monitoring:res.usagePctFull'), min: 0, max: 100 } };
    const memoryTrendPanel = { chartType: 'line', xField: 't', yFields: memoryTrendSeries, yAxis: { title: 'MB' } };
    const tempTrendPanel = { chartType: 'line', xField: 't', yFields: tempTrendSeries, yAxis: { title: t('monitoring:res.usagePctFull'), min: 0, max: 100 } };
    const undoTrendPanel = { chartType: 'line', xField: 't', yFields: undoTrendSeries, yAxis: { title: 'blocks' } };
    const segmentTrendPanel = { chartType: 'line', xField: 't', yFields: segmentTrendSeries, yAxis: { title: 'logical reads(delta)' } };
    const historyWindowMin = historyDays * 24 * 60;

    return (
        <ConnTabPane active={active} toolbar={<OwnConnTarget hideConn
                conns={mon.conns.filter(c => c.id === mon.connId || !excludeConnIds.includes(c.id))} connId={mon.connId} onConnChange={mon.setConnId}
                sessionId={mon.sessionId} session={mon.session}
                sessionError={mon.sessionError} creating={monCreating}
                onPickSession={mon.pickSession}
            />}>
            {queryViewer}
            {helpViewer}

                <div className="resana-tabs">
                    <button
                        className={`resana-tab ${activeTab === 'realtime' ? 'active' : ''}`}
                        onClick={() => setActiveTab('realtime')}
                    >
                        <FiHardDrive size={13} /> {t('monitoring:res.tab.live')}
                    </button>
                    <button
                        className={`resana-tab ${activeTab === 'history' ? 'active' : ''}`}
                        onClick={() => setActiveTab('history')}
                    >
                        <FiClock size={13} /> {t('monitoring:res.tab.history')}
                    </button>
                    <div className="resana-tabs-spacer" />
                    {/* 연결 선택은 제목줄(OwnConnTarget)로 옮겼다 — 다섯 창이 같은 자리다 */}
                    {/* 설명 보기 — 끄면 본문의 풀이 문장(관리 방식 설명·힌트·안내)만 감춘다. 도움말(?) 창은 그대로(2026-10-03 요청) */}
                    <label className="resana-demo-toggle">
                        <input type="checkbox" checked={showNotes} onChange={(e) => toggleNotes(e.target.checked)} />
                        {t('monitoring:res.showNotes')}
                    </label>
                    <label className="resana-demo-toggle">
                        <input type="checkbox" checked={useDemo} onChange={(e) => setUseDemo(e.target.checked)} />
                        {t('monitoring:res.demoData')}
                    </label>
                </div>


                <div className={`resana-body ${showNotes ? '' : 'resana-hide-notes'} ${activeTab === 'realtime' && visibleSections.storage ? 'resana-body-fit' : ''}`}>
                    {activeTab === 'realtime' && (
                        <div className="resana-fade-swap resana-layout">
                            <div className="resana-toolbar resana-toolbar-sticky">
                                <button className="resana-btn resana-btn-secondary" onClick={() => { fetchStorage(); fetchMemory(); fetchUndo(); fetchTemp(); fetchSegments(); }} disabled={storageLoading || memoryLoading || undoLoading || tempLoading || segmentLoading}>
                                    <FiRefreshCw size={12} className={(storageLoading || memoryLoading || undoLoading || tempLoading || segmentLoading) ? 'resana-spin' : ''} /> {t('monitoring:res.refresh')}
                                </button>
                                <div className="resana-section-toggles" role="tablist">
                                    <button type="button" className={`resana-section-toggle ${visibleSections.storage ? 'on' : ''}`} onClick={() => toggleSection('storage')}>{t('monitoring:res.h.tablespace')}</button>
                                    <button type="button" className={`resana-section-toggle ${visibleSections.memory ? 'on' : ''}`} onClick={() => toggleSection('memory')}>{t('monitoring:res.h.sga')}</button>
                                    <button type="button" className={`resana-section-toggle ${visibleSections.pga ? 'on' : ''}`} onClick={() => toggleSection('pga')}>{t('monitoring:res.pga')}</button>
                                    <button type="button" className={`resana-section-toggle ${visibleSections.undo ? 'on' : ''}`} onClick={() => toggleSection('undo')}>{t('monitoring:res.h.undo')}</button>
                                    <button type="button" className={`resana-section-toggle ${visibleSections.temp ? 'on' : ''}`} onClick={() => toggleSection('temp')}>{t('monitoring:res.h.temp')}</button>
                                    <button type="button" className={`resana-section-toggle ${visibleSections.segments ? 'on' : ''}`} onClick={() => toggleSection('segments')}>{t('monitoring:res.h.segStats')}</button>
                                </div>
                            </div>

                            {visibleSections.storage && (
                            <div className="resana-chart-card resana-ts-card">
                                <div className="resana-chart-title">
                                    {t('monitoring:res.h.tablespace')}
                                    {storageLoading && <span className="resana-loading-badge">{t('monitoring:res.loading')}</span>}
                                    {storageIsDemo && !storageLoading && <span className="resana-demo-badge">{t('monitoring:res.demoData')}</span>}
                                    <button className="resana-btn resana-btn-icon" onClick={fetchStorage} disabled={storageLoading} title={t('monitoring:res.refresh')}>
                                        <FiRefreshCw size={11} className={storageLoading ? 'resana-spin' : ''} />
                                    </button>
                                    {queryBtn('storage')}
                                </div>
                                {storageError && (
                                    <div className="resana-section-error">
                                        <FiInfo size={13} /> {storageError}
                                        <button className="resana-btn resana-btn-small" onClick={fetchStorage}>{t('monitoring:res.retry')}</button>
                                    </div>
                                )}
                                <div className="resana-sort-group">
                                    <span className="resana-label">{t('monitoring:res.sortBy')}</span>
                                    {STORAGE_SORT_OPTIONS.map(o => (
                                        <button
                                            key={o.value}
                                            type="button"
                                            className={`resana-sort-btn ${storageSortBy === o.value ? 'on' : ''}`}
                                            onClick={() => setStorageSortBy(o.value)}
                                        >
                                            {o.label}
                                        </button>
                                    ))}
                                    {/* 전체보기는 위에 — 아래에 두면 목록이 길어질 때 그 자리를 찾기 어렵다(요청) */}
                                    <span className="resana-sort-spacer" />
                                    {showAllBtn(sortedStorageRows.length, storageShowAll, () => setStorageShowAll(v => !v))}
                                </div>
                                {/* 그림과 표를 한 격자에 — 줄마다 막대와 수치가 나란히 선다(요청). 줄을 누르면 아래에 그 세그먼트.
                                    목록과 세그먼트 칸은 높이가 정해져 있고 각자 스크롤한다 — 창 전체에는 세로 스크롤이 없다(요청) */}
                                <div className="resana-ts-list">
                                    <UsageBarGrid
                                        rows={displayedStorageRows}
                                        metric={storageSortBy}
                                        labels={gridLabels}
                                        fmtMb={fmtMb} fmtPct={fmtPct}
                                        emptyText={storageLoading ? '' : t('monitoring:res.noData')}
                                        selectedKey={selectedTs}
                                        onRowClick={(r) => setSelectedTs(prev => (prev === r.tablespaceName ? null : r.tablespaceName))}
                                        rowHint={t('monitoring:res.tsSeg.pickHint')}
                                    />
                                </div>
                                <div className="resana-ts-seg">
                                    <div className="resana-chart-title">
                                        {selectedTs ? t('monitoring:res.tsSeg.title', { ts: selectedTs }) : t('monitoring:res.tsSeg.titleAll')}
                                        {selectedTs && (
                                            <button type="button" className="resana-btn resana-btn-small" onClick={() => setSelectedTs(null)} title={t('monitoring:res.tsSeg.showAllHint')}>
                                                {t('monitoring:res.tsSeg.showAll')}
                                            </button>
                                        )}
                                        {tsSegLoading && <span className="resana-loading-badge">{t('monitoring:res.loading')}</span>}
                                        {tsSeg?.isDemo && !tsSegLoading && <span className="resana-demo-badge">{t('monitoring:res.demoData')}</span>}
                                        <button className="resana-btn resana-btn-icon" onClick={fetchTsSegments} disabled={tsSegLoading} title={t('monitoring:res.refresh')}>
                                            <FiRefreshCw size={11} className={tsSegLoading ? 'resana-spin' : ''} />
                                        </button>
                                        {queryBtn('ts-segments')}
                                    </div>
                                    <div className="resana-sort-group">
                                        <span className="resana-label">{t('monitoring:res.segUnit')}</span>
                                        {[['object', t('monitoring:res.segUnitObject')], ['subobject', t('monitoring:res.segUnitSub')]].map(([v, label]) => (
                                            <button key={v} type="button" className={`resana-sort-btn ${tsSegGran === v ? 'on' : ''}`} onClick={() => setTsSegGran(v)}>{label}</button>
                                        ))}
                                        <span className="resana-sort-spacer" />
                                        {tsSeg?.total?.count != null && (
                                            <span className="resana-hint">{t('monitoring:res.tsSeg.summary', {
                                                shown: tsSeg.rows.length, total: tsSeg.total.count, size: fmtMb(tsSeg.total.mb),
                                            })}</span>
                                        )}
                                    </div>
                                    {tsSegError && <div className="resana-section-error"><FiInfo size={13} /> {tsSegError}</div>}
                                    <div className="resana-ts-seg-scroll">
                                        <table className="resana-table resana-seg-table">
                                            <thead>
                                                <tr>
                                                    <th className="resana-seg-barhead">{t('monitoring:res.tsSeg.size')}</th>
                                                    {!selectedTs && <th>{t('monitoring:res.tablespace')}</th>}
                                                    <th>Owner</th><th>{t('monitoring:res.tsSeg.segment')}</th>
                                                    {tsSegGran === 'subobject' && <th>{t('monitoring:res.tsSeg.partition')}</th>}
                                                    <th>Type</th>
                                                    {tsSegGran === 'object' && <th className="resana-num" title={t('monitoring:res.tsSeg.piecesHint')}>{t('monitoring:res.tsSeg.pieces')}</th>}
                                                    <th className="resana-num" title={t('monitoring:res.tsSeg.shareHint')}>{t('monitoring:res.tsSeg.share')}</th>
                                                    <th className="resana-num">{t('monitoring:res.tsSeg.extents')}</th>
                                                    <th className="resana-num">{t('monitoring:res.tsSeg.blocks')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(tsSeg?.rows || []).map((r, i) => {
                                                    const maxMb = Math.max(1, ...(tsSeg.rows || []).map(x => Number(x.mb) || 0));
                                                    const share = tsSeg.total?.mb > 0 ? (Number(r.mb) / tsSeg.total.mb) * 100 : null;
                                                    return (
                                                        <tr key={`${r.tablespace || ''}.${r.owner}.${r.segmentName}.${r.partitionName || ''}.${r.segmentType}-${i}`}>
                                                            <td className="resana-seg-barcol">
                                                                <span className="resana-ugrid-barcell">
                                                                    <span className="resana-ugrid-bar amount" style={{ width: `${Math.min(100, ((Number(r.mb) || 0) / maxMb) * 100)}%` }} />
                                                                    <span className="resana-ugrid-barval">{fmtMb(r.mb)}</span>
                                                                </span>
                                                            </td>
                                                            {!selectedTs && <td className="resana-mono">{r.tablespace || '-'}</td>}
                                                            <td className="resana-mono">{r.owner}</td>
                                                            <td className="resana-mono resana-ellipsis" title={r.segmentName}>{r.segmentName}</td>
                                                            {tsSegGran === 'subobject' && <td className="resana-mono">{r.partitionName || '-'}</td>}
                                                            <td>{r.segmentType}</td>
                                                            {tsSegGran === 'object' && <td className="resana-num">{r.pieces ?? '-'}</td>}
                                                            <td className="resana-num">{share == null ? '-' : fmtPct(share)}</td>
                                                            <td className="resana-num">{r.extents != null ? Number(r.extents).toLocaleString() : '-'}</td>
                                                            <td className="resana-num">{r.blocks != null ? Number(r.blocks).toLocaleString() : '-'}</td>
                                                        </tr>
                                                    );
                                                })}
                                                {tsSeg && (tsSeg.rows || []).length === 0 && !tsSegLoading && (
                                                    <tr><td colSpan={10} className="resana-empty-note">{t('monitoring:res.tsSeg.none')}</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                            )}

                            {visibleSections.memory && (
                            <div className="resana-chart-card">
                                <div className="resana-chart-title">
                                    {t('monitoring:res.h.sga')}
                                    {memoryLoading && <span className="resana-loading-badge">{t('monitoring:res.loading')}</span>}
                                    {memoryIsDemo && !memoryLoading && <span className="resana-demo-badge">{t('monitoring:res.demoData')}</span>}
                                    <button className="resana-btn resana-btn-icon" onClick={fetchMemory} disabled={memoryLoading} title={t('monitoring:res.refresh')}>
                                        <FiRefreshCw size={11} className={memoryLoading ? 'resana-spin' : ''} />
                                    </button>
                                    {queryBtn('sga')}
                                </div>
                                {memoryError && (
                                    <div className="resana-section-error">
                                        <FiInfo size={13} /> {memoryError}
                                        <button className="resana-btn resana-btn-small" onClick={fetchMemory}>{t('monitoring:res.retry')}</button>
                                    </div>
                                )}
                                {/* 노드마다 트리맵 — 상자 크기 = 그 노드 SGA 중 몫(요청). buffer_cache 는 이제 이름으로 보인다 */}
                                <div className="resana-node-grid resana-node-grid-aligned">
                                    {sgaNodes.map(g => {
                                        const pools = memoryBufferPools.filter(p => (p.instId ?? 1) === g.instId);
                                        const usage = (bufferUsage || []).filter(u => (u.instId ?? 1) === g.instId);
                                        const used = usage.find(u => u.state === 'used');
                                        const free = usage.find(u => u.state === 'free');
                                        const sum = (Number(used?.mb) || 0) + (Number(free?.mb) || 0);
                                        return (
                                            <div key={g.instId} className="resana-node-col">
                                                <NodeHead instId={g.instId} multi={sgaNodes.length > 1} t={t}>
                                                    <span className="resana-node-sub">{t('monitoring:res.sgaTotal', { size: fmtMb(g.rows.reduce((x, r) => x + (Number(r.mb) || 0), 0)) })}</span>
                                                </NodeHead>
                                                <MemoryModeNote kind="sga" params={memParamsByInst.get(g.instId)} t={t} fmtMb={fmtMb} />
                                                {/* 칸은 늘 넷(머리·관리 방식·트리맵·버퍼 정보) — 노드 열끼리 같은 줄에 서게(subgrid, 2026-10-03) */}
                                                <div className="resana-node-slot">
                                                    <SgaTreemap components={g.rows} freeLabel={t('monitoring:res.freeShort')} />
                                                </div>
                                                <div className="resana-node-slot">
                                                {pools.length > 0 && (
                                                    <div className="resana-bufpool">
                                                        <b>{t('monitoring:res.bufferCache')}</b>
                                                        {pools.map(p => (
                                                            <span key={`${p.name}-${p.blockSize}`} className="resana-bufpool-item">
                                                                {`${p.name}${p.blockSize ? ` ${Math.round(p.blockSize / 1024)}K` : ''} ${fmtMb(p.mb)}`}
                                                                {p.hitPct != null && <span className={p.hitPct < 90 ? 'resana-pct-warn' : ''}>{` · ${t('monitoring:res.hit')} ${fmtPct(p.hitPct)}`}</span>}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                                {used && sum > 0 && (
                                                    <div className="resana-bufusage">
                                                        <div className="resana-bufusage-bar">
                                                            <span style={{ width: `${(Number(used.mb) / sum) * 100}%` }} />
                                                        </div>
                                                        <span>{t('monitoring:res.bufUsed', { used: fmtMb(used.mb), free: fmtMb(free?.mb || 0), pct: fmtPct((Number(used.mb) / sum) * 100) })}</span>
                                                    </div>
                                                )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {memorySga.length === 0 && !memoryLoading && <div className="resana-empty-note">{t('monitoring:res.noData')}</div>}
                                </div>
                                {/* 실제로 차 있는 버퍼 수는 누를 때만 센다(gv$bh 는 무겁다) */}
                                <div className="resana-bufusage-actions">
                                    <button type="button" className="resana-btn resana-btn-secondary" onClick={fetchBufferUsage} disabled={bufferUsageLoading}>
                                        <FiRefreshCw size={11} className={bufferUsageLoading ? 'resana-spin' : ''} /> {t('monitoring:res.countBufUsage')}
                                    </button>
                                    <span className="resana-hint">{t('monitoring:res.countBufUsageHint')}</span>
                                    {queryBtn('buffer-usage')}
                                </div>
                                {bufferUsageError && <div className="resana-section-error"><FiInfo size={13} /> {bufferUsageError}</div>}
                            </div>
                            )}

                            {visibleSections.pga && (
                            <div className="resana-chart-card">
                                <div className="resana-chart-title">
                                    {t('monitoring:res.pga')}
                                    {memoryLoading && <span className="resana-loading-badge">{t('monitoring:res.loading')}</span>}
                                    {memoryIsDemo && !memoryLoading && <span className="resana-demo-badge">{t('monitoring:res.demoData')}</span>}
                                    <button className="resana-btn resana-btn-icon" onClick={fetchMemory} disabled={memoryLoading} title={t('monitoring:res.refresh')}>
                                        <FiRefreshCw size={11} className={memoryLoading ? 'resana-spin' : ''} />
                                    </button>
                                    {queryBtn('pga')}
                                </div>
                                {/* 노드별로 한 줄씩 — 예전에는 2노드의 카드가 노드 표시 없이 이어 붙었다 */}
                                {pgaNodes.map(g => (
                                    <div key={g.instId} className="resana-node-row">
                                        <NodeHead instId={g.instId} multi={pgaNodes.length > 1} t={t} />
                                        <MemoryModeNote kind="pga" params={memParamsByInst.get(g.instId)} t={t} fmtMb={fmtMb} />
                                        <div className="resana-pga-cards">
                                            {g.rows.map(p => (
                                                <div key={`${g.instId}-${p.name}`} className="resana-pga-card">
                                                    <div className="resana-pga-value">{fmtMb(p.mb)}</div>
                                                    <div className="resana-pga-label">{p.name}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                                {memoryPga.length === 0 && !memoryLoading && <div className="resana-empty-note">{t('monitoring:res.noData')}</div>}
                                {/* TEMP 상위 세션과 같은 꼴 — 「누가 지금 PGA 를 많이 쥐고 있나」(할당 순) */}
                                <div className="resana-subtitle">{t('monitoring:res.pgaTopSessions')}</div>
                                {pgaConsumersError ? (
                                    <div className="resana-section-error"><FiInfo size={13} /> {t('monitoring:res.pgaTopFailed', { message: pgaConsumersError })}</div>
                                ) : (
                                <div className="resana-table-wrap">
                                <table className="resana-table">
                                    <thead>
                                        <tr>
                                            <th>{t('monitoring:res.nodeCol')}</th><th>SID</th><th>{t('monitoring:res.user')}</th><th>{t('monitoring:res.program')}</th>
                                            <th>{t('monitoring:res.statusCol')}</th>
                                            <th className="resana-num" title={t('monitoring:res.pgaAllocHint')}>{t('monitoring:res.pgaAlloc')}</th>
                                            <th className="resana-num" title={t('monitoring:res.pgaUsedHint')}>{t('monitoring:res.pgaUsed')}</th>
                                            <th className="resana-num" title={t('monitoring:res.pgaMaxHint')}>{t('monitoring:res.pgaMax')}</th>
                                            <th>SQL_ID</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pgaConsumers.map((c, i) => (
                                            <tr key={`${c.instId}-${c.sid}-${i}`}>
                                                <td className="resana-mono">{c.instId ?? 1}</td>
                                                <td className="resana-mono">{c.sid}{c.serial ? `,${c.serial}` : ''}</td>
                                                <td>{c.username}</td>
                                                <td className="resana-ellipsis" title={c.program || ''}>{c.program || '-'}</td>
                                                <td>{c.status}</td>
                                                <td className="resana-num">{fmtMb(c.allocMb)}</td>
                                                <td className="resana-num">{fmtMb(c.usedMb)}</td>
                                                <td className="resana-num">{fmtMb(c.maxMb)}</td>
                                                <td className="resana-mono">{sqlIdCell('pga', c.sqlId)}</td>
                                            </tr>
                                        ))}
                                        {pgaConsumers.length === 0 && !memoryLoading && (
                                            <tr><td colSpan={9} className="resana-empty-note">{t('monitoring:res.noPgaSessions')}</td></tr>
                                        )}
                                    </tbody>
                                </table>
                                </div>
                                )}
                                {sqlTextPanel('pga')}
                            </div>
                            )}

                            {visibleSections.undo && (
                            <div className="resana-chart-card">
                                <div className="resana-chart-title">
                                    {t('monitoring:res.h.undo')}
                                    {undoLoading && <span className="resana-loading-badge">{t('monitoring:res.loading')}</span>}
                                    {undoIsDemo && !undoLoading && <span className="resana-demo-badge">{t('monitoring:res.demoData')}</span>}
                                    <button className="resana-btn resana-btn-icon" onClick={fetchUndo} disabled={undoLoading} title={t('monitoring:res.refresh')}>
                                        <FiRefreshCw size={11} className={undoLoading ? 'resana-spin' : ''} />
                                    </button>
                                    {queryBtn('undo')}
                                </div>
                                {undoError && (
                                    <div className="resana-section-error">
                                        <FiInfo size={13} /> {undoError}
                                        <button className="resana-btn resana-btn-small" onClick={fetchUndo}>{t('monitoring:res.retry')}</button>
                                    </div>
                                )}
                                {/* 노드마다 — 예전에는 첫 노드만 보였다. 블록 구성은 그림(막대)으로(요청) */}
                                {undoNodes.map(n => (
                                    <div key={n.instId} className="resana-node-row">
                                        <NodeHead instId={n.instId} multi={undoNodes.length > 1} t={t} />
                                        <UndoBlocksBar latest={n.latest} labels={{
                                            active: t('monitoring:res.undoActive'), unexpired: t('monitoring:res.undoUnexpired'),
                                            expired: t('monitoring:res.undoExpired'), retention: t('monitoring:res.undoRetention'),
                                            none: t('monitoring:res.noData'),
                                        }} />
                                        {n.tablespace && (
                                            <UsageBarGrid
                                                rows={[n.tablespace]} metric="usedPct"
                                                labels={{ ...gridLabels, name: t('monitoring:res.undo') }}
                                                fmtMb={fmtMb} fmtPct={fmtPct} emptyText=""
                                            />
                                        )}
                                        {(n.latest?.ssolderrcnt > 0 || n.latest?.nospaceerrcnt > 0) && (
                                            <div className="resana-error">
                                                <FiInfo size={13} /> {t('monitoring:res.undoErrors', { ssol: n.latest.ssolderrcnt, nospace: n.latest.nospaceerrcnt })}
                                            </div>
                                        )}
                                    </div>
                                ))}
                                {undoNodes.length === 0 && !undoLoading && <div className="resana-empty-note">{t('monitoring:res.noData')}</div>}
                                {/* PGA·TEMP 상위 세션과 같은 꼴 — 「누가 지금 UNDO 를 많이 쥐고 있나」(진행 중 트랜잭션, 사용 블록 순) */}
                                <div className="resana-subtitle">{t('monitoring:res.undoTopTx')}</div>
                                <div className="resana-hint">{t('monitoring:res.undoTopTxHint')}</div>
                                {undoTxError ? (
                                    <div className="resana-section-error"><FiInfo size={13} /> {t('monitoring:res.undoTopTxFailed', { message: undoTxError })}</div>
                                ) : (
                                <div className="resana-table-wrap">
                                <table className="resana-table">
                                    <thead>
                                        <tr>
                                            <th>{t('monitoring:res.nodeCol')}</th><th>SID</th><th>{t('monitoring:res.user')}</th><th>{t('monitoring:res.program')}</th>
                                            <th>{t('monitoring:res.statusCol')}</th>
                                            <th className="resana-num">{t('monitoring:res.undoUsed')}</th>
                                            <th className="resana-num">{t('monitoring:res.undoBlocks')}</th>
                                            <th className="resana-num" title={t('monitoring:res.undoRecordsHint')}>{t('monitoring:res.undoRecords')}</th>
                                            <th className="resana-num">{t('monitoring:res.txElapsed')}</th>
                                            <th>{t('monitoring:res.tablespace')}</th>
                                            <th>SQL_ID</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {undoTx.map((x, i) => (
                                            <tr key={`${x.instId}-${x.sid}-${i}`}>
                                                <td className="resana-mono">{x.instId ?? 1}</td>
                                                <td className="resana-mono">{x.sid}{x.serial ? `,${x.serial}` : ''}</td>
                                                <td>{x.username}</td>
                                                <td className="resana-ellipsis" title={x.program || ''}>{x.program || '-'}</td>
                                                <td>{x.status}</td>
                                                <td className="resana-num">{fmtMb(x.usedMb)}</td>
                                                <td className="resana-num">{x.usedBlocks != null ? Number(x.usedBlocks).toLocaleString() : '-'}</td>
                                                <td className="resana-num">{x.usedRecords != null ? Number(x.usedRecords).toLocaleString() : '-'}</td>
                                                <td className="resana-num" title={x.startTime ? t('monitoring:res.txStarted', { time: x.startTime.replace('T', ' ') }) : ''}>{fmtElapsed(x.elapsedSec)}</td>
                                                <td className="resana-mono">{x.tablespaceName || '-'}</td>
                                                {/* 지금 도는 SQL 이 없으면(DML 을 끝내고 커밋 전) 직전 SQL — 섞지 않고 「직전」 이라 표시 */}
                                                <td className="resana-mono">
                                                    {x.sqlId ? sqlIdCell('undo', x.sqlId) : (x.prevSqlId ? <span title={t('monitoring:res.prevSqlHint')}>{sqlIdCell('undo', x.prevSqlId)} <span className="resana-prev-tag">{t('monitoring:res.prevSql')}</span></span> : '-')}
                                                </td>
                                            </tr>
                                        ))}
                                        {undoTx.length === 0 && !undoLoading && (
                                            <tr><td colSpan={11} className="resana-empty-note">{t('monitoring:res.noUndoTx')}</td></tr>
                                        )}
                                    </tbody>
                                </table>
                                </div>
                                )}
                                {sqlTextPanel('undo')}
                            </div>
                            )}

                            {visibleSections.temp && (
                            <div className="resana-chart-card">
                                <div className="resana-chart-title">
                                    {t('monitoring:res.h.temp')}
                                    {tempLoading && <span className="resana-loading-badge">{t('monitoring:res.loading')}</span>}
                                    {tempIsDemo && !tempLoading && <span className="resana-demo-badge">{t('monitoring:res.demoData')}</span>}
                                    <button className="resana-btn resana-btn-icon" onClick={fetchTemp} disabled={tempLoading} title={t('monitoring:res.refresh')}>
                                        <FiRefreshCw size={11} className={tempLoading ? 'resana-spin' : ''} />
                                    </button>
                                    {queryBtn('temp')}
                                </div>
                                {tempError && (
                                    <div className="resana-section-error">
                                        <FiInfo size={13} /> {tempError}
                                        <button className="resana-btn resana-btn-small" onClick={fetchTemp}>{t('monitoring:res.retry')}</button>
                                    </div>
                                )}
                                {/* 노드별 — 공유 TEMP 중 그 노드가 쥔 몫(gv$sort_segment). 한 번도 안 쓴 노드는 줄이 없다 */}
                                {tempNodes.map(g => (
                                    <div key={g.instId} className="resana-node-row">
                                        <NodeHead instId={g.instId} multi={tempNodes.length > 1} t={t} />
                                        <UsageBarGrid rows={g.rows} metric="usedPct" labels={gridLabels} fmtMb={fmtMb} fmtPct={fmtPct} emptyText="" />
                                    </div>
                                ))}
                                {tempTablespaces.length === 0 && !tempLoading && <div className="resana-empty-note">{t('monitoring:res.noTempUse')}</div>}
                                <div className="resana-subtitle">{t('monitoring:res.tempTopSessions')}</div>
                                <div className="resana-table-wrap">
                                <table className="resana-table">
                                    <thead>
                                        <tr><th>{t('monitoring:res.nodeCol')}</th><th>SID</th><th>{t('monitoring:res.user')}</th><th>{t('monitoring:res.tablespace')}</th><th>{t('monitoring:res.usage')}</th><th>SQL_ID</th></tr>
                                    </thead>
                                    <tbody>
                                        {tempConsumers.map((c, i) => (
                                            <tr key={`${c.instId}-${c.sid}-${i}`}>
                                                <td className="resana-mono">{c.instId ?? 1}</td>
                                                <td className="resana-mono">{c.sid}{c.serial ? `,${c.serial}` : ''}</td>
                                                <td>{c.username}</td>
                                                <td className="resana-mono">{c.tablespace}</td>
                                                <td>{fmtMb(c.mb)}</td>
                                                <td className="resana-mono">{sqlIdCell('temp', c.sqlId)}</td>
                                            </tr>
                                        ))}
                                        {tempConsumers.length === 0 && !tempLoading && (
                                            <tr><td colSpan={6} className="resana-empty-note">{t('monitoring:res.noTempSessions')}</td></tr>
                                        )}
                                    </tbody>
                                </table>
                                </div>
                                {sqlTextPanel('temp')}
                            </div>
                            )}

                            {visibleSections.segments && (
                            <div className="resana-chart-card">
                                <div className="resana-chart-title">
                                    {t('monitoring:res.h.segStats')}
                                    {segmentLoading && <span className="resana-loading-badge">{t('monitoring:res.loading')}</span>}
                                    {segmentIsDemo && !segmentLoading && <span className="resana-demo-badge">{t('monitoring:res.demoData')}</span>}
                                    <button className="resana-btn resana-btn-icon" onClick={fetchSegments} disabled={segmentLoading} title={t('monitoring:res.refresh')}>
                                        <FiRefreshCw size={11} className={segmentLoading ? 'resana-spin' : ''} />
                                    </button>
                                    {queryBtn('segments')}
                                </div>
                                {segmentError && (
                                    <div className="resana-section-error">
                                        <FiInfo size={13} /> {segmentError}
                                        <button className="resana-btn resana-btn-small" onClick={fetchSegments}>{t('monitoring:res.retry')}</button>
                                    </div>
                                )}
                                {/* 집계 단위와 정렬 기준 — 기본이 서브오브젝트(파티션) 단위여서
                                    목록 20칸이 파티션 하나짜리 표로 가득 찼다. 기본을 오브젝트로 두고
                                    쪼개 보는 것은 선택으로 남긴다. */}
                                <div className="resana-sort-group">
                                    <span className="resana-label">{t('monitoring:res.segUnit')}</span>
                                    {[['object', t('monitoring:res.segUnitObject')], ['subobject', t('monitoring:res.segUnitSub')]].map(([v, label]) => (
                                        <button
                                            key={v}
                                            type="button"
                                            className={`resana-sort-btn ${segGranularity === v ? 'on' : ''}`}
                                            onClick={() => setSegGranularity(v)}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                    <span className="resana-label">{t('monitoring:res.sortBy')}</span>
                                    {SEGMENT_SORT_OPTIONS(t).map(o => (
                                        <button
                                            key={o.value}
                                            type="button"
                                            className={`resana-sort-btn ${segOrderBy === o.value ? 'on' : ''}`}
                                            onClick={() => setSegOrderBy(o.value)}
                                        >
                                            {o.label}
                                        </button>
                                    ))}
                                    <span className="resana-sort-spacer" />
                                    {showAllBtn(segmentRows.length, segmentShowAll, () => setSegmentShowAll(v => !v))}
                                </div>
                                {/* 그림과 표를 한 표에(2026-10-03 요청 — 테이블스페이스와 같게): 왼쪽 칸은 정렬 기준 지표의 막대,
                                    오른쪽은 표 내용 그대로. 예전에는 그래프와 표가 따로라 줄이 맞지 않았다. */}
                                <div className="resana-table-wrap resana-seg-wrap">
                                <table className="resana-table resana-seg-table">
                                    <thead>
                                        <tr>
                                            <th className="resana-seg-barhead">{segMetricLabel}</th>
                                            {segMulti && <th>{t('monitoring:res.nodeCol')}</th>}
                                            <th>Owner</th><th>Object</th><th>Type</th><th>Tablespace</th>
                                            <th className="num" title={t('monitoring:res.segSizeHint')}>{t('monitoring:res.tsSeg.size')}</th>
                                            {SEGMENT_SORT_OPTIONS(t).map(o => (
                                                <th key={o.value} className={`num ${segOrderBy === o.value ? 'resana-seg-sorted' : ''}`}>{SEG_COL_HEAD[o.value]}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {displayedSegmentRows.map((s, i) => {
                                            const v = Number(s[segMetricField]) || 0;
                                            return (
                                                <tr key={`${s.instId}-${s.owner}.${s.objectName}.${s.subobjectName || ''}-${i}`}>
                                                    <td className="resana-seg-barcol">
                                                        <span className="resana-ugrid-barcell">
                                                            <span className="resana-ugrid-bar amount" style={{ width: `${Math.min(100, (v / segBarMax) * 100)}%` }} />
                                                            <span className="resana-ugrid-barval">{fmtCount(s[segMetricField])}</span>
                                                        </span>
                                                    </td>
                                                    {segMulti && <td className="resana-mono">{s.instId ?? 1}</td>}
                                                    <td className="resana-mono">{s.owner}</td>
                                                    <td className="resana-mono">{s.objectName}{s.subobjectName ? ` (${s.subobjectName})` : ''}</td>
                                                    <td>{s.objectType}</td>
                                                    <td className="resana-mono">{s.tablespaceName}</td>
                                                    <td className="num">{s.sizeMb == null ? '-' : fmtMb(s.sizeMb)}</td>
                                                    {SEGMENT_SORT_OPTIONS(t).map(o => (
                                                        <td key={o.value} className={`num ${segOrderBy === o.value ? 'resana-seg-sorted' : ''}`}>{fmtCount(s[SEGMENT_FIELD[o.value]])}</td>
                                                    ))}
                                                </tr>
                                            );
                                        })}
                                        {segmentRows.length === 0 && !segmentLoading && (
                                            <tr><td colSpan={segMulti ? 11 : 10} className="resana-empty-note">{t('monitoring:res.noData')}</td></tr>
                                        )}
                                    </tbody>
                                </table>
                                </div>
                            </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'history' && (
                        <div className="resana-fade-swap resana-layout">
                            <div className="resana-toolbar resana-toolbar-sticky">
                                <select className="resana-select" value={historyDays} onChange={(e) => setHistoryDays(Number(e.target.value))}>
                                    {getDaysOptions(t).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                                <button className="resana-btn resana-btn-secondary" onClick={() => { fetchStorageTrend(); fetchMemoryTrend(); fetchUndoTrend(); fetchSegmentTrend(); }} disabled={storageTrendLoading || memoryTrendLoading || undoTrendLoading || segmentTrendLoading}>
                                    <FiRefreshCw size={12} className={(storageTrendLoading || memoryTrendLoading || undoTrendLoading || segmentTrendLoading) ? 'resana-spin' : ''} /> {t('monitoring:res.refresh')}
                                </button>
                                <div className="resana-section-toggles" role="tablist">
                                    <button type="button" className={`resana-section-toggle ${visibleTrends.storage ? 'on' : ''}`} onClick={() => toggleTrend('storage')}>{t('monitoring:res.h.tablespaceTrend')}</button>
                                    <button type="button" className={`resana-section-toggle ${visibleTrends.memory ? 'on' : ''}`} onClick={() => toggleTrend('memory')}>{t('monitoring:res.h.sgaPgaTrend')}</button>
                                    <button type="button" className={`resana-section-toggle ${visibleTrends.undo ? 'on' : ''}`} onClick={() => toggleTrend('undo')}>{t('monitoring:res.h.undoTrend')}</button>
                                    <button type="button" className={`resana-section-toggle ${visibleTrends.temp ? 'on' : ''}`} onClick={() => toggleTrend('temp')}>{t('monitoring:res.h.tempTrend')}</button>
                                    <button type="button" className={`resana-section-toggle ${visibleTrends.segments ? 'on' : ''}`} onClick={() => toggleTrend('segments')}>{t('monitoring:res.h.segTop5Trend')}</button>
                                </div>
                                <span className="resana-toolbar-note">
                                    <FiInfo size={12} /> {t('monitoring:res.note.awr')}
                                </span>
                            </div>

                            {visibleTrends.storage && (
                            <div className="resana-chart-card">
                                <div className="resana-chart-title">
                                    {t('monitoring:res.h.tablespaceTrend')}
                                    {storageTrendLoading && <span className="resana-loading-badge">{t('monitoring:res.loading')}</span>}
                                    <button className="resana-btn resana-btn-icon" onClick={fetchStorageTrend} disabled={storageTrendLoading} title={t('monitoring:res.refresh')}>
                                        <FiRefreshCw size={11} className={storageTrendLoading ? 'resana-spin' : ''} />
                                    </button>
                                </div>
                                {storageTrendError && (
                                    <div className="resana-section-error">
                                        <FiInfo size={13} /> {storageTrendError}
                                        <button className="resana-btn resana-btn-small" onClick={fetchStorageTrend}>{t('monitoring:res.retry')}</button>
                                    </div>
                                )}
                                <PanelChart panel={storageTrendPanel} data={storageTrend} height={220} isDarkMode={darkMode} timeWindowMin={historyWindowMin} displayMode="sync" />
                            </div>
                            )}

                            {visibleTrends.memory && (
                            <div className="resana-chart-card">
                                <div className="resana-chart-title">
                                    {t('monitoring:res.h.sgaPgaTrend')}
                                    {memoryTrendLoading && <span className="resana-loading-badge">{t('monitoring:res.loading')}</span>}
                                    <button className="resana-btn resana-btn-icon" onClick={fetchMemoryTrend} disabled={memoryTrendLoading} title={t('monitoring:res.refresh')}>
                                        <FiRefreshCw size={11} className={memoryTrendLoading ? 'resana-spin' : ''} />
                                    </button>
                                </div>
                                {memoryTrendError && (
                                    <div className="resana-section-error">
                                        <FiInfo size={13} /> {memoryTrendError}
                                        <button className="resana-btn resana-btn-small" onClick={fetchMemoryTrend}>{t('monitoring:res.retry')}</button>
                                    </div>
                                )}
                                <PanelChart panel={memoryTrendPanel} data={memoryTrend} height={220} isDarkMode={darkMode} timeWindowMin={historyWindowMin} displayMode="sync" />
                            </div>
                            )}

                            {visibleTrends.undo && (
                            <div className="resana-chart-card">
                                <div className="resana-chart-title">
                                    {t('monitoring:res.h.undoTrend')}
                                    {undoTrendLoading && <span className="resana-loading-badge">{t('monitoring:res.loading')}</span>}
                                    <span className="resana-toolbar-note" style={{ marginLeft: 8 }}>
                                        <FiInfo size={12} /> {t('monitoring:res.note.vbased')}
                                    </span>
                                    <button className="resana-btn resana-btn-icon" onClick={fetchUndoTrend} disabled={undoTrendLoading} title={t('monitoring:res.refresh')}>
                                        <FiRefreshCw size={11} className={undoTrendLoading ? 'resana-spin' : ''} />
                                    </button>
                                </div>
                                {undoTrendError && (
                                    <div className="resana-section-error">
                                        <FiInfo size={13} /> {undoTrendError}
                                        <button className="resana-btn resana-btn-small" onClick={fetchUndoTrend}>{t('monitoring:res.retry')}</button>
                                    </div>
                                )}
                                <PanelChart panel={undoTrendPanel} data={undoTrend} height={220} isDarkMode={darkMode} timeWindowMin={historyWindowMin} displayMode="sync" />
                            </div>
                            )}

                            {visibleTrends.temp && (
                            <div className="resana-chart-card">
                                <div className="resana-chart-title">
                                    {t('monitoring:res.h.tempTrend')}
                                    {tempTrendLoading && <span className="resana-loading-badge">{t('monitoring:res.loading')}</span>}
                                    <button className="resana-btn resana-btn-icon" onClick={fetchTempTrend} disabled={tempTrendLoading} title={t('monitoring:res.refresh')}>
                                        <FiRefreshCw size={11} className={tempTrendLoading ? 'resana-spin' : ''} />
                                    </button>
                                    {queryBtn('temp-trend')}
                                </div>
                                {tempTrendError && (
                                    <div className="resana-section-error">
                                        <FiInfo size={13} /> {tempTrendError}
                                        <button className="resana-btn resana-btn-small" onClick={fetchTempTrend}>{t('monitoring:res.retry')}</button>
                                    </div>
                                )}
                                {/* 기록이 없는 것과 "TEMP 를 안 썼다" 는 다른 말이다 — 0 으로 채우지 않고 그렇게 적는다. */}
                                {!tempTrendLoading && !tempTrendError && tempTrend.length === 0 ? (
                                    <div className="resana-empty-note">{t('monitoring:res.noTempTrend')}</div>
                                ) : (
                                    <PanelChart panel={tempTrendPanel} data={tempTrend} height={220} isDarkMode={darkMode} timeWindowMin={historyWindowMin} displayMode="sync" />
                                )}
                            </div>
                            )}

                            {visibleTrends.segments && (
                            <div className="resana-chart-card">
                                <div className="resana-chart-title">
                                    {t('monitoring:res.h.segTop5Trend')}
                                    {segmentTrendLoading && <span className="resana-loading-badge">{t('monitoring:res.loading')}</span>}
                                    <button className="resana-btn resana-btn-icon" onClick={fetchSegmentTrend} disabled={segmentTrendLoading} title={t('monitoring:res.refresh')}>
                                        <FiRefreshCw size={11} className={segmentTrendLoading ? 'resana-spin' : ''} />
                                    </button>
                                </div>
                                {segmentTrendError && (
                                    <div className="resana-section-error">
                                        <FiInfo size={13} /> {segmentTrendError}
                                        <button className="resana-btn resana-btn-small" onClick={fetchSegmentTrend}>{t('monitoring:res.retry')}</button>
                                    </div>
                                )}
                                <PanelChart panel={segmentTrendPanel} data={segmentTrend} height={220} isDarkMode={darkMode} timeWindowMin={historyWindowMin} displayMode="sync" />
                            </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="resana-footer">
                    <button className="app-btn resana-btn resana-btn-secondary" onClick={onClose}>{t('monitoring:res.close')}</button>
                </div>
        </ConnTabPane>
    );
};


/**
 * 창 — **DB 연결마다 탭**(2026-10-14 요청: 여러 DB 를 동시에 조회). 탭마다 위의 본문 하나(자기 연결의 전담 세션).
 */
const ResourceAnalysisModal = (props) => {
    const { t } = useTranslation();
    return (
    <ConnTabsWindow sessionLabel="resource-analysis"
        isOpen={props.isOpen} onClose={props.onClose} connections={props.connections}
        defaultConnectionId={props.defaultConnectionId} filter={ORACLE_CONN}
        icon={<FiHardDrive size={14} />} title={t('monitoring:res.title')} overlayClassName="resana-overlay" className="resana-modal" headerClassName="resana-header" closeClassName="resana-close"
        renderBody={(tab) => <ResourceAnalysisModalBody {...props} {...tab} />}
    />
    );
};

// 작업 탭을 오갈 때 Header 가 새 콜백을 넘겨도 본문을 다시 그리지 않는다(common/memoWindow)
export default memoWindow(ResourceAnalysisModal);
